import { useGetStaffPaginated } from "@/hooks/staff/use-staff";
import {
  type MRT_ColumnDef,
  type MRT_ColumnFiltersState,
  MRT_ToggleFiltersButton,
} from "material-react-table";
import { useMemo } from "react";

import { APIPaginatedTable } from "@/components/common/api-paginated-table";
import type { APIPaginatedTableState } from "@/components/common/api-paginated-table/states";
import type { Staff } from "@/types/staff/api-types/staff-types";
import { Button } from "@/components/ui/button";
import { Trash2 } from "lucide-react";

const createStaffFilters = (columnFilters: MRT_ColumnFiltersState) => {
  const filters: {
    first_name?: string;
    last_name?: string;
    level_name?: string;
  } = {};

  const getTrimmedString = (value: unknown) =>
    typeof value === "string" ? value.trim() : "";

  columnFilters.forEach((filter) => {
    const colName = filter.id.split(".")[1] ?? filter.id;
    const trimmedValue = getTrimmedString(filter.value);
    if (trimmedValue === "") return;

    if (colName === "first_name") {
      filters.first_name = trimmedValue;
    }
    if (colName === "last_name") {
      filters.last_name = trimmedValue;
    }
    if (colName === "level_name") {
      filters.level_name = trimmedValue;
    }
  });

  return filters;
};

interface StaffTableProps {
  onStaffClick: (staff: Staff) => void;
  onDeleteClick: (staff: Staff) => void;
}

const StaffTable = ({
  onStaffClick,
  onDeleteClick,
}: StaffTableProps) => {
  const columns = useMemo<MRT_ColumnDef<Staff>[]>(
    () => [
      {
        accessorKey: "first_name",
        header: "First Name",
        Cell: ({ row, renderedCellValue }) => {
          const staff = row.original;
          return (
            <button
              onClick={() => onStaffClick?.(staff)}
              className="text-purple-500 hover:underline cursor-pointer"
            >
              {renderedCellValue}
            </button>
          );
        },
      },
      {
        accessorKey: "last_name",
        header: "Last Name",
      },
      {
        accessorKey: "level_name",
        header: "Level",
        Cell: ({ renderedCellValue }) => {
          return renderedCellValue || "Unknown Level";
        },
      },
      {
        id: "actions",
        header: "Actions",
        Cell: ({ row }) => {
          const staff = row.original;
          return (
            <Button
              variant="ghost"
              size="sm"
              onClick={() => onDeleteClick(staff)}
            >
              <Trash2 className="h-4 w-4" />
            </Button>
          );
        },
      },
    ],
    [onStaffClick, onDeleteClick]
  );

  return (
    <>
      <APIPaginatedTable
        columns={columns}
        reactQuery={useGetStaffPaginated}
        reactQueryFilters={(state: APIPaginatedTableState) => {
          const filters = createStaffFilters(state.columnFilters);

          return {
            page: state.pagination.pageIndex,
            size: state.pagination.pageSize,
            ...filters,
          };
        }}
        onRenderToolbarInternalActions={({ table }) => (
          <>
            <MRT_ToggleFiltersButton table={table} />
          </>
        )}
        titleTopic="staff"
        contentTopic="staff member"
      />
    </>
  );
};

export default StaffTable;